import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bell, ArrowRight } from 'lucide-react';
import { MagneticButton } from './MagneticButton';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Newsletter = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.post(`${API}/newsletter`, { email, name: name || null });
      toast.success('Welcome to Buttershine! You\'ll be the first to know about new releases.');
      setEmail('');
      setName('');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('This email is already subscribed!');
      } else {
        toast.error('Failed to subscribe. Please try again.');
      }
      console.error('Newsletter error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section data-testid="newsletter-section" className="py-24 lg:py-32 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#2C2C2C] to-[#3D3D3D]" />
      
      {/* Decorative elements */}
      <motion.div
        animate={{ 
          scale: [1, 1.2, 1],
          opacity: [0.1, 0.2, 0.1]
        }}
        transition={{ duration: 8, repeat: Infinity }}
        className="absolute top-10 left-10 w-64 h-64 rounded-full bg-[#DAB668]/20 blur-3xl"
      />
      <motion.div
        animate={{ 
          scale: [1.2, 1, 1.2],
          opacity: [0.1, 0.2, 0.1]
        }}
        transition={{ duration: 10, repeat: Infinity }}
        className="absolute bottom-10 right-10 w-96 h-96 rounded-full bg-[#DAB668]/10 blur-3xl"
      />

      <div className="max-w-4xl mx-auto px-6 lg:px-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-2 bg-[#DAB668]/20 text-[#DAB668] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Bell size={16} />
            <span>Stay Updated</span>
          </div>

          <h2 data-testid="newsletter-heading" className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
            Be the First to Know
          </h2>

          <p className="text-white/70 max-w-xl mx-auto mb-10">
            Join our community and get exclusive early access to new product launches, 
            special offers, and wellness tips straight to your inbox.
          </p>
        </motion.div>

        <motion.form
          data-testid="newsletter-form"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          onSubmit={handleSubmit}
          className="max-w-2xl mx-auto"
        >
          <div className="flex flex-col gap-4">
            {/* Name and Email row */}
            <div className="flex flex-col sm:flex-row gap-4">
              <input
                type="text"
                data-testid="newsletter-name-input"
                placeholder="Your Name (Optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="flex-1 px-6 py-4 bg-white/10 border border-white/20 rounded-full text-white placeholder-white/50 focus:outline-none focus:border-[#DAB668] transition-colors"
              />
              <input
                type="email"
                data-testid="newsletter-email-input"
                placeholder="Enter your email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 px-6 py-4 bg-white/10 border border-white/20 rounded-full text-white placeholder-white/50 focus:outline-none focus:border-[#DAB668] transition-colors"
              />
            </div>
            
            {/* Subscribe button - full width on mobile, centered on desktop */}
            <div className="flex justify-center">
              <MagneticButton
                type="submit"
                data-testid="newsletter-submit-btn"
                disabled={isLoading}
                className="w-full sm:w-auto px-12 py-4 bg-[#DAB668] text-white rounded-full font-medium hover:bg-[#C5A152] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isLoading ? 'Joining...' : (
                  <>
                    Subscribe
                    <ArrowRight size={18} />
                  </>
                )}
              </MagneticButton>
            </div>
          </div>
        </motion.form>

        <motion.p 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-6 text-white/50 text-sm text-center"
        >
          We respect your privacy. Unsubscribe anytime.
        </motion.p>
      </div>
    </section>
  );
};

export default Newsletter;
